import { logInfo, logWarn } from "@/helpers/logError";

export type SparkyColdStartPhase =
	| "auth_client_acquire"
	| "token_silent_fetch"
	| "scribe_status_headers"
	| "scribe_status_body"
	| "scribe_status_complete"
	| "scribe_auth_skipped";
type NavigationKind = "hard_navigation" | "reload" | "back_forward" | "client_navigation" | "unknown";
type PhaseOutcome = "success" | "transient_failure" | "terminal_failure" | "skipped";
type PhaseContext = {
	cacheMode?: "default" | "cache_only" | "off";
	attempt?: number;
	joinedInFlight?: boolean;
	status?: number;
	routeType?: "project" | "conversation" | "other";
};
type Dependencies = {
	now: () => number;
	createId: () => string;
	getNavigationKind: () => NavigationKind;
	info: typeof logInfo;
	warn: typeof logWarn;
};

const SLOW_PHASE_MS = 2000;

function defaultNavigationKind(): NavigationKind {
	const navigation = performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined;
	if (navigation?.type === "reload") return "reload";
	if (navigation?.type === "back_forward") return "back_forward";
	if (navigation?.type === "navigate") return "hard_navigation";
	return "unknown";
}

export function createSparkyColdStartDiagnostics(dependencies: Dependencies) {
	let bootCorrelationId = "boot-unavailable";
	try {
		bootCorrelationId = dependencies.createId();
	} catch {
		// Diagnostics construction must not prevent auth or Scribe modules from loading.
	}
	const claimedPhases = new Set<SparkyColdStartPhase>();
	function claim(phase: SparkyColdStartPhase): boolean {
		if (claimedPhases.has(phase)) return false;
		claimedPhases.add(phase);
		return true;
	}
	function startPhase(phase: SparkyColdStartPhase, context: PhaseContext = {}) {
		let startedAt: number;
		try {
			startedAt = dependencies.now();
		} catch {
			return { finish: () => undefined };
		}
		let finished = false;
		return {
			finish(outcome: PhaseOutcome): void {
				if (finished) return;
				finished = true;
				try {
					const elapsedMs = Math.max(0, Math.round(dependencies.now() - startedAt));
					const emittedOutcome = outcome === "success" && elapsedMs >= SLOW_PHASE_MS ? "slow" : outcome;
					const payload: Record<string, unknown> = {
						bootCorrelationId,
						phase,
						outcome: emittedOutcome,
						elapsedMs,
						navigationKind: dependencies.getNavigationKind()
					};
					if (context.cacheMode !== undefined) payload.cacheMode = context.cacheMode;
					if (context.attempt !== undefined) payload.attempt = context.attempt;
					if (context.joinedInFlight !== undefined) payload.joinedInFlight = context.joinedInFlight;
					if (context.status !== undefined) payload.status = context.status;
					if (context.routeType !== undefined) payload.routeType = context.routeType;
					(emittedOutcome === "slow" ? dependencies.warn : dependencies.info)("scribe_cold_start_timing", payload);
				} catch {
					// Diagnostics are observational and must never affect auth or request behavior.
				}
			}
		};
	}
	return { claim, startPhase };
}

const createId = () => "boot-" + Date.now() + "-" + Math.random().toString(16).slice(2);

export const sparkyColdStartDiagnostics = createSparkyColdStartDiagnostics({
	now: () => performance.now(),
	createId,
	getNavigationKind: defaultNavigationKind,
	info: (message, context) => logInfo(message, context),
	warn: (message, context) => logWarn(message, context)
});
